import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, Calendar, Video } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollCount >= 5) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <Card className="card-marketing max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === "checking" && (
            <>
              <div className="animate-spin w-12 h-12 border-4 border-accent border-t-transparent rounded-full mx-auto mb-6"></div>
              <h1 className="font-heading text-2xl text-text-primary mb-4">Processing Payment...</h1>
              <p className="text-text-secondary">Please wait while we confirm your payment.</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h1 className="font-heading text-2xl text-text-primary mb-4">Booking Confirmed!</h1>
              <p className="text-text-secondary mb-8">
                Your consultation has been scheduled. You will receive a confirmation email with the Zoom meeting link.
              </p>
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 text-text-secondary">
                  <Calendar className="w-5 h-5 text-accent" />
                  <span>Calendar invite sent to your email</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-text-secondary">
                  <Video className="w-5 h-5 text-accent" />
                  <span>Zoom link included in the invite</span>
                </div>
              </div>
              <Link to="/">
                <Button className="btn-primary mt-8" data-testid="return-home-btn">
                  Return to Home
                </Button>
              </Link>
            </>
          )}

          {status === "expired" && (
            <>
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-red-500 text-2xl">!</span>
              </div>
              <h1 className="font-heading text-2xl text-text-primary mb-4">Session Expired</h1>
              <p className="text-text-secondary mb-8">
                Your payment session has expired. Please try booking again.
              </p>
              <Link to="/booking">
                <Button className="btn-primary" data-testid="try-again-btn">
                  Try Again
                </Button>
              </Link>
            </>
          )}

          {status === "timeout" && (
            <>
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-yellow-500 text-2xl">?</span>
              </div>
              <h1 className="font-heading text-2xl text-text-primary mb-4">Status Unknown</h1>
              <p className="text-text-secondary mb-8">
                We couldn't confirm your payment status. Please check your email for confirmation or contact us.
              </p>
              <Link to="/">
                <Button className="btn-primary" data-testid="return-home-btn">
                  Return to Home
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
